// src/pages/PagePanier.jsx

/*
|--------------------------------------------------------------------------
| Bloc 1 — imports React et dépendances projet
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce fichier orchestre :
| - l’affichage du panier
| - l’audit pricing des lignes
| - les recommandations contextuelles
| - l’ouverture de la modale produit depuis une recommandation
|--------------------------------------------------------------------------
*/
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { usePanier } from "../contexts/PanierContext";
import { useProfil } from "../contexts/ProfilContext";
import { useProfilPricing } from "../hooks/useProfilPricing";
import { auditPrice } from "../utils/priceAuditor";
import { formatCHF } from "../utils/formatCHF";
import ProductQuickViewModal from "../components/Modals/ProductQuickViewModal";
import { trackFunnelEventBatch } from "../utils/trackFunnelBatch";

/*
|--------------------------------------------------------------------------
| Bloc 2 — configuration animation suppression
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Cette constante garde la durée de la micro-animation visuelle avant
| suppression effective d’une ligne panier.
|--------------------------------------------------------------------------
*/
const REMOVE_ANIM_MS = 280;

/*
|--------------------------------------------------------------------------
| Bloc 3 — images fallback panier
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ces images servent de secours si une ligne panier ou une recommandation
| n’a pas d’image exploitable.
|--------------------------------------------------------------------------
*/
const DEFAULT_PRODUCT_IMAGE = "https://marchedeluxe.ch/images/placeholder.png";

const DEFAULT_PLATEAU_IMAGE =
  "https://marchedeluxe.ch/images/plateau-evenement-default.png";

/*
|--------------------------------------------------------------------------
| Bloc 4 — helper détection produit cave / alcool
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper reconnaît les lignes cave / alcool pour afficher la notice
| légale correspondante dans le résumé panier.
|--------------------------------------------------------------------------
*/
const isAlcoholCartItem = (item) => {
  if (!item || typeof item !== "object") return false;

  if (item.__isCave === true) return true;
  if (item.__productScope === "cave") return true;
  if (item.product_scope === "cave") return true;
  if (Number(item.isCave || 0) === 1) return true;

  return false;
};

/*
|--------------------------------------------------------------------------
| Bloc 5 — helper détection plateau
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper identifie les lignes panier plateau événement afin d’appliquer
| le bon rendu et le bon fallback image.
|--------------------------------------------------------------------------
*/
const isPlateauCartItem = (item) => {
  if (!item || typeof item !== "object") return false;

  if (item.isPlateau === true) return true;
  if (item.typeProduit === "plateau-evenement") return true;
  if (item.category === "plateaux-evenements") return true;

  return false;
};
const isFondueCartItem = (item) => {
  if (!item || typeof item !== "object") return false;

  return (
    item.isFondue === true ||
    item.typeProduit === "fondue-composee" ||
    item.item_type === "fondue-traditionnelle" ||
    item.fondueConfig?.mode === "traditionnelle"
  );
};

/*
|--------------------------------------------------------------------------
| Bloc 6 — helper normalisation image panier
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper reçoit une ligne panier ou un produit recommandé et retourne
| une URL image exploitable par <img>.
|--------------------------------------------------------------------------
*/
const resolveCartItemImage = (item) => {
  const raw =
    item?.imageUrl ||
    item?.image_url ||
    item?.image ||
    item?.photo ||
    item?.thumbnail ||
    "";

  const plateauFallback = isPlateauCartItem(item);

  if (!raw || String(raw).trim() === "") {
    return plateauFallback ? DEFAULT_PLATEAU_IMAGE : DEFAULT_PRODUCT_IMAGE;
  }

  const str = String(raw).trim();

  if (str.startsWith("http://") || str.startsWith("https://")) {
    return str;
  }

  if (str.startsWith("/images/")) {
    return `https://marchedeluxe.ch${str}`;
  }

  if (str.startsWith("images/")) {
    return `https://marchedeluxe.ch/${str}`;
  }

  if (str.startsWith("/")) {
    return `https://marchedeluxe.ch${str}`;
  }

  return `https://marchedeluxe.ch/images/${str}`;
};

/*
|--------------------------------------------------------------------------
| Bloc 7 — helper libellés types de lait
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper convertit les valeurs techniques de plateau en libellés lisibles
| côté interface.
|--------------------------------------------------------------------------
*/
const getMilkAvoidLabels = (values = [], t = {}) => {
  const map = {
    cru: t?.plateauRawMilk || "Lait cru",
    thermise: t?.plateauThermizedMilk || "Lait thermisé",
    pasteurise: t?.plateauPasteurizedMilk || "Lait pasteurisé",
  };

  return values.map((item) => map[item] || item);
};

/*
|--------------------------------------------------------------------------
| Bloc 8 — helper normalisation texte unité
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Uniformiser les unités produit pour les détections métier :
| - trim
| - lower case
| - espaces propres
|--------------------------------------------------------------------------
*/
const normalizeUnitText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/*
|--------------------------------------------------------------------------
| Bloc 9 — helper détection unité "pièce"
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Reconnaître les différentes variantes d’unités simples vendues à la pièce.
|--------------------------------------------------------------------------
*/
const isPieceLikeUnit = (value) => {
  const u = normalizeUnitText(value);

  return (
    u === "pièce" ||
    u === "pièces" ||
    u === "piece" ||
    u === "pieces" ||
    u === "unité" ||
    u === "unite" ||
    u === "unit" ||
    u === "units" ||
    u === "pcs" ||
    u.includes("pièce") ||
    u.includes("piece") ||
    u.includes("unité") ||
    u.includes("unite")
  );
};

/*
|--------------------------------------------------------------------------
| Bloc 10 — helper extraction poids fixe depuis unité
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Certains produits n’utilisent pas item.grams mais embarquent un poids dans
| l’unité :
| - 100g
| - 250 g
| - 0.5kg
|--------------------------------------------------------------------------
*/
const parseFixedGramsFromUnit = (value) => {
  const u = normalizeUnitText(value).replace(",", ".");

  const match = u.match(/(\d+(\.\d+)?)\s*(kg|g)\b/);
  if (!match) return null;

  const rawValue = Number(match[1]);
  const rawUnit = match[3];

  if (!Number.isFinite(rawValue) || rawValue <= 0) return null;

  return rawUnit === "kg" ? Math.round(rawValue * 1000) : Math.round(rawValue);
};

/*
|--------------------------------------------------------------------------
| Bloc 10.bis — helper détection produit vendu au poids
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper détecte si une ligne panier est concernée par l’information de
| préparation au poids :
| - poids libre via item.grams
| - poids fixe détecté dans l’unité
|--------------------------------------------------------------------------
*/
const isWeightedCartItem = (item) => {
  if (!item || typeof item !== "object") return false;

  const explicitGrams = Number(item?.grams || 0) || 0;
  if (explicitGrams > 0) return true;

  const rawUnit = item?.unitLabel || item?.unite || "";
  const fixedGrams = parseFixedGramsFromUnit(rawUnit);

  if (Number.isFinite(fixedGrams) && fixedGrams > 0) {
    return true;
  }

  return false;
};

/*
|--------------------------------------------------------------------------
| Bloc 11 — helper détection sémantique d’unité
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper produit une lecture UX plus propre des unités pour l’affichage
| du résumé de quantité.
|--------------------------------------------------------------------------
*/
const detectUnitSemantic = (item) => {
  const unitLabel = normalizeUnitText(item?.unitLabel || item?.unite || "");
  const productName = normalizeUnitText(item?.name || item?.nom || "");

  if (
    unitLabel.includes("bouteille") ||
    unitLabel.includes("btl") ||
    productName.includes("bouteille")
  ) {
    return "bouteille";
  }

  if (unitLabel.includes("pot") || productName.includes("pot")) {
    return "pot";
  }

  if (unitLabel.includes("bocal") || productName.includes("bocal")) {
    return "bocal";
  }

  if (
    unitLabel.includes("yaourt") ||
    unitLabel.includes("yogourt") ||
    productName.includes("yaourt") ||
    productName.includes("yogourt")
  ) {
    return "pot";
  }

  if (isPieceLikeUnit(unitLabel)) {
    return "pièce";
  }

  if (unitLabel) {
    return unitLabel;
  }

  return "unité";
};

/*
|--------------------------------------------------------------------------
| Bloc 12 — helper pluriel simple
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper produit un libellé de quantité total lisible :
| - 1 bouteille
| - 2 bouteilles
|--------------------------------------------------------------------------
*/
const pluralizeUnit = (singular, qty) => {
  const count = Number(qty || 0);

  if (count <= 1) return singular;

  const normalized = normalizeUnitText(singular);

  if (normalized === "bouteille") return "bouteilles";
  if (normalized === "pot") return "pots";
  if (normalized === "bocal") return "bocaux";
  if (normalized === "pièce") return "pièces";
  if (normalized === "unité") return "unités";

  if (normalized.endsWith("s")) return singular;
  return `${singular}s`;
};

/*
|--------------------------------------------------------------------------
| Bloc 13 — helper affichage poids
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Afficher proprement les poids :
| - 100g
| - 250g
| - 1kg
| - 1.5kg
|--------------------------------------------------------------------------
*/
const formatWeightLabel = (grams) => {
  const safeGrams = Number(grams || 0);

  if (!Number.isFinite(safeGrams) || safeGrams <= 0) return "";

  if (safeGrams >= 1000 && safeGrams % 1000 === 0) {
    return `${safeGrams / 1000}kg`;
  }

  if (safeGrams >= 1000) {
    const kg = safeGrams / 1000;
    return `${String(kg).replace(/\.0$/, "")}kg`;
  }

  return `${safeGrams}g`;
};

/*
|--------------------------------------------------------------------------
| Bloc 14 — helper méta unité ligne panier
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce helper résume la logique d’affichage :
| - prix unitaire
| - détail secondaire
| - quantité totale affichable
|--------------------------------------------------------------------------
*/
const getCartItemUnitMeta = (item) => {
  const explicitGrams = Number(item?.grams || 0) || 0;
  const qty = Number(item?.qty || item?.quantite || 1) || 1;
  const rawUnit = item?.unitLabel || item?.unite || "";
  const fixedGrams = parseFixedGramsFromUnit(rawUnit);
  const semanticUnit = detectUnitSemantic(item);

  if (explicitGrams > 0) {
    return {
      mode: "weight",
      qty,
      unitPriceLabel: "/ 100g",
      secondaryLabel: formatWeightLabel(explicitGrams),
      totalQuantityLabel: formatWeightLabel(explicitGrams * qty),
    };
  }

  if (Number.isFinite(fixedGrams) && fixedGrams > 0) {
    return {
      mode: "fixed-grams",
      qty,
      unitPriceLabel: `/ ${formatWeightLabel(fixedGrams)}`,
      secondaryLabel:
        qty > 1
          ? `${qty} × ${formatWeightLabel(fixedGrams)}`
          : formatWeightLabel(fixedGrams),
      totalQuantityLabel: formatWeightLabel(fixedGrams * qty),
    };
  }

  return {
    mode: "unit",
    qty,
    unitPriceLabel: rawUnit ? `/ ${rawUnit}` : "/ unité",
    secondaryLabel: null,
    totalQuantityLabel: `${qty} ${pluralizeUnit(semanticUnit, qty)}`,
  };
  if (isFondueCartItem(item)) {
    const peopleCount = Number(item?.fondueConfig?.peopleCount || 0);
    const totalGrams = Number(item?.fondueConfig?.totalGrams || 0);

    return {
      mode: "fondue",
      qty: Number(item?.qty || 1) || 1,
      unitPriceLabel: "",
      secondaryLabel: peopleCount ? `Pour ${peopleCount} personnes` : null,
      totalQuantityLabel: totalGrams ? `${totalGrams}g` : "Fondue préparée",
    };
  }
};

/*
|--------------------------------------------------------------------------
| Bloc 15 — helper clé source de recommandation
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Fabriquer la clé utilisée pour retrouver les recommandations liées à une
| ligne panier :
| - classic:254
| - cave:12
| - plateau
|--------------------------------------------------------------------------
*/
const getRecommendationSourceKey = (item) => {
  if (!item || typeof item !== "object") return "";

  if (isPlateauCartItem(item)) {
    return "plateau";
  }

  const scope =
    item?.scope ||
    item?.product_scope ||
    item?.__productScope ||
    (isAlcoholCartItem(item) ? "cave" : "classic");

  const id = Number(item?.id || 0);
  if (!id) return "";

  return `${scope === "cave" ? "cave" : "classic"}:${id}`;
};

/*
|--------------------------------------------------------------------------
| Bloc 16 — helper payload minimal pour endpoint recommandations
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Réduire la ligne panier à l’essentiel avant envoi à l’endpoint de
| recommandations.
|--------------------------------------------------------------------------
*/
const buildRecommendationRequestItem = (item) => ({
  id: Number(item?.id || 0),
  scope:
    item?.scope ||
    item?.product_scope ||
    item?.__productScope ||
    (isAlcoholCartItem(item) ? "cave" : "classic"),
  product_scope: item?.product_scope || item?.__productScope || "",
  __isCave: Boolean(item?.__isCave || item?.isCave),
  isCave: Boolean(item?.isCave),
  isPlateau: Boolean(item?.isPlateau),
  typeProduit: item?.typeProduit || "",
});

/*
|--------------------------------------------------------------------------
| Bloc 17 — helper normalisation produit recommandé -> objet panier
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Le backend renvoie un produit simplifié.
| Ce helper le reconstruit pour l’ajout direct au panier.
|--------------------------------------------------------------------------
*/
const normalizeRecommendedProductForCart = (product) => {
  const priceCents = Math.round(Number(product?.price_cents || 0));
  const imageUrl = resolveCartItemImage(product);

  return {
    id: Number(product?.id || 0),
    name: product?.name || "",
    nom: product?.name || "",
    title: product?.name || "",

    price: priceCents / 100,
    prix: priceCents / 100,
    displayPrice: priceCents / 100,
    appliedPrice: priceCents / 100,
    basePrice: priceCents / 100,
    publicPrice: priceCents / 100,

    qty: 1,
    quantite: 1,
    quantity: 1,

    image: imageUrl,
    imageUrl,
    image_url: imageUrl,

    category: product?.category || "",
    unite: product?.unite || "",

    __isCave: Boolean(product?.__isCave || product?.scope === "cave"),
    __productScope:
      product?.scope ||
      product?.__productScope ||
      product?.product_scope ||
      "classic",
    product_scope:
      product?.scope ||
      product?.__productScope ||
      product?.product_scope ||
      "classic",
    isCave: product?.scope === "cave" ? 1 : 0,
  };
};

/*
|--------------------------------------------------------------------------
| Bloc 18 — helper produit recommandé -> produit modale
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Le clic sur le nom d’une recommandation ouvre une vraie modale produit.
| Ce helper construit un objet compatible avec ProductQuickViewModal.
|--------------------------------------------------------------------------
*/
const normalizeRecommendedProductForModal = (product) => {
  const priceCents = Math.round(Number(product?.price_cents || 0));
  const imageUrl = resolveCartItemImage(product);

  return {
    id: Number(product?.id || 0),
    name: product?.name || "",
    nom: product?.name || "",
    title: product?.name || "",
    category: product?.category || "",
    image: imageUrl,
    imageUrl,
    image_url: imageUrl,
    unite: product?.unite || "pièce",
    price: priceCents / 100,
    priceCents,
    stock: Number(product?.stock || 0) || 0,
    isOnline: Number(product?.stock || 0) > 0 ? 1 : 1,
    isDiscount: Number(product?.isDiscount || 0) || 0,
    old_price: product?.old_price ?? null,
    pricePro: product?.pricePro ?? null,
    __isCave: Boolean(product?.__isCave || product?.scope === "cave"),
    __productScope:
      product?.scope ||
      product?.__productScope ||
      product?.product_scope ||
      "classic",
    product_scope:
      product?.scope ||
      product?.__productScope ||
      product?.product_scope ||
      "classic",
    description: product?.description || "",
    histoire: product?.histoire || "",
  };
};

/*
|--------------------------------------------------------------------------
| Bloc 19 — composant reco inline unique
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Cette version affiche :
| - le nom du produit recommandé
| - un petit bouton +
|
| Le nom ouvre la modale produit.
| Le + ajoute directement au panier.
|--------------------------------------------------------------------------
*/
const InlineRecommendationItem = ({ product, onAdd, onOpen, t }) => {
  return (
    <div className="cart-reco-inline-item">
      <button
        type="button"
        className="cart-reco-inline-link"
        onClick={() => onOpen?.(product)}
        title={product?.name || t?.recommendedProduct || "Produit recommandé"}
      >
        {product?.name || ""}
      </button>

      <button
        type="button"
        className="cart-reco-inline-add"
        onClick={() => onAdd?.(product)}
        aria-label={`${t?.addToCart || "Ajouter"} ${product?.name || ""}`}
        title={t?.addToCart || "Ajouter"}
      >
        +
      </button>
    </div>
  );
};

/*
|--------------------------------------------------------------------------
| Bloc 20 — composant bloc de recommandations inline
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce composant affiche les recommandations liées à une ligne panier si
| une source correspondante existe.
|--------------------------------------------------------------------------
*/
const CartRecommendations = ({
  cartItem,
  recommendationsBySourceKey,
  onAddRecommendation,
  onOpenRecommendation,
  t,
}) => {
  const sourceKey = useMemo(
    () => getRecommendationSourceKey(cartItem),
    [cartItem]
  );

  const entry = sourceKey ? recommendationsBySourceKey?.[sourceKey] : null;
  const products = Array.isArray(entry?.items) ? entry.items : [];

  if (!products.length) return null;

  const resolveRecommendationTitle = (rawTitle, t = {}) => {
    if (!rawTitle) {
      return t?.cartRecommendationsInlineTitle || "À savourer avec";
    }

    if (rawTitle === "cartRecommendationsInlineTitle") {
      return t?.cartRecommendationsInlineTitle || "À savourer avec";
    }

    if (rawTitle === "cartRecommendationsTitle") {
      return t?.cartRecommendationsTitle || "À savourer avec";
    }

    return rawTitle;
  };

  const title = resolveRecommendationTitle(entry?.title, t);

  return (
    <div className="cart-reco-inline-block">
      <span className="cart-reco-inline-title">{title}</span>

      <div className="cart-reco-inline-list">
        {products.map((product) => (
          <InlineRecommendationItem
            key={`${product?.scope || "classic"}:${product?.id || 0}`}
            product={product}
            onAdd={onAddRecommendation}
            onOpen={onOpenRecommendation}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

/*
|--------------------------------------------------------------------------
| Bloc 21 — composant ligne panier
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce composant gère le rendu complet d’une ligne :
| - image
| - quantité
| - prix unitaire
| - total ligne
| - détails plateau
| - recommandations liées
|--------------------------------------------------------------------------
*/
const PanierItem = ({
  item,
  onUpdateQty,
  onRemove,
  t,
  recommendationsBySourceKey,
  onAddRecommendation,
  onOpenRecommendation,
}) => {
  const { audit } = item;
  const [isRemoving, setIsRemoving] = useState(false);

  const imageUrl = useMemo(() => resolveCartItemImage(item), [item]);

  const laitsEviterLabels = useMemo(() => {
    return getMilkAvoidLabels(item?.options?.laitsEviter || [], t);
  }, [item?.options?.laitsEviter, t]);

  const unitMeta = useMemo(() => getCartItemUnitMeta(item), [item]);

  const handleRemove = () => {
    if (isRemoving) return;
    setIsRemoving(true);

    window.setTimeout(() => {
      onRemove(item.id, item.grams ?? null);
    }, REMOVE_ANIM_MS);
  };

  const grams = Number(item?.grams || 0) || 0;
  const gramsMultiplier = grams > 0 ? grams / 100 : 1;

  const lineTotalCents = Math.round(
    Number(audit.displayPrice || 0) * gramsMultiplier * Number(item.qty || 1)
  );

  return (
    <div className={`panier-item-shell ${isRemoving ? "is-removing" : ""}`}>
      <div className={`panier-item ${isRemoving ? "is-removing" : ""}`}>
        <div className="panier-item-media">
          <div className="panier-item-img-container item-img">
            <img
              src={imageUrl}
              alt={item.name || item.nom || "Produit"}
              className="panier-item-img"
              onError={(e) => {
                e.currentTarget.src = isPlateauCartItem(item)
                  ? DEFAULT_PLATEAU_IMAGE
                  : DEFAULT_PRODUCT_IMAGE;
              }}
            />
          </div>

          <div className="panier-item-controls item-quantity">
            <button
              className="qty-btn"
              onClick={() => onUpdateQty(item.id, -1, item.grams ?? null)}
              type="button"
              aria-label={t?.decreaseQty || "Diminuer"}
              disabled={isRemoving}
            >
              -
            </button>

            <span className="qty-value">{item.qty || item.quantite || 1}</span>

            <button
              className="qty-btn"
              onClick={() => onUpdateQty(item.id, 1, item.grams ?? null)}
              type="button"
              aria-label={t?.increaseQty || "Augmenter"}
              disabled={isRemoving}
            >
              +
            </button>
          </div>
        </div>

        <div className="panier-item-info item-info">
          <h3>{item.name || item.nom}</h3>
          <p className="panier-item-price item-price">
            {formatCHF(audit.displayPrice)} {unitMeta.unitPriceLabel}
            {unitMeta.secondaryLabel ? (
              <span className="panier-unit-secondary">
                • {unitMeta.secondaryLabel}
              </span>
            ) : null}
          </p>
          <div className="panier-inline-summary">
            <span className="panier-inline-quantity">
              {unitMeta.totalQuantityLabel}
            </span>
            <span className="panier-inline-total">
              {formatCHF(lineTotalCents)}
            </span>
          </div>
          {(item.options || item.details_label || item.fondueConfig) && (
            <div
              className="panier-options"
              style={{
                fontSize: 13,
                marginTop: 8,
                color: "#666",
                lineHeight: 1.5,
              }}
            >
              {item.options?.personnes ? (
                <div>
                  <strong>
                    {t?.plateauPeopleCount || "Nombre de personnes"} :
                  </strong>{" "}
                  {item.options.personnes}
                </div>
              ) : null}

              {item.options?.fromages ? (
                <div>
                  <strong>{t?.plateauPreferences || "Préférences"} :</strong>{" "}
                  {item.options.fromages}
                </div>
              ) : null}

              {Array.isArray(item.options?.laitsEviter) &&
              item.options.laitsEviter.length > 0 ? (
                <div>
                  <strong>{t?.plateauAvoid || "À éviter"} :</strong>{" "}
                  {laitsEviterLabels.join(", ")}
                </div>
              ) : null}

              {item?.details_label ? (
                <div className="panier-fondue-details">
                  <strong>
                    {isFondueCartItem(item)
                      ? "Préparation magasin"
                      : t?.plateauDetails || "Détails"}{" "}
                    :
                  </strong>{" "}
                  {item.details_label}
                </div>
              ) : null}
            </div>
          )}{" "}
        </div>

        <button
          className="btn-remove"
          onClick={handleRemove}
          title={t?.remove || "Supprimer"}
          type="button"
          disabled={isRemoving}
        >
          ✕
        </button>
      </div>

      <CartRecommendations
        cartItem={item}
        recommendationsBySourceKey={recommendationsBySourceKey}
        onAddRecommendation={onAddRecommendation}
        onOpenRecommendation={onOpenRecommendation}
        t={t}
      />
    </div>
  );
};

/*
|--------------------------------------------------------------------------
| Bloc 22 — composant principal PagePanier
|--------------------------------------------------------------------------
| RÔLE
| -------------------------------------------------------------------------
| Ce composant orchestre :
| - la lecture du panier global
| - l’audit pricing des lignes
| - le total affiché
| - les recommandations panier
| - l’ouverture de modale recommandation
| - les notices métier du résumé
|--------------------------------------------------------------------------
*/
const PagePanier = ({ t = {}, setVue }) => {
  const {
    panier: items = [],
    updateQuantite,
    retirerDuPanier,
    ajouterAuPanier,
  } = usePanier();

  const { userProfil, authStatus } = useProfil();
  const profilPricing = useProfilPricing(userProfil);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.1 — audit de toutes les lignes panier
  --------------------------------------------------------------------------
  */
  const itemsAudites = useMemo(() => {
    return (Array.isArray(items) ? items : []).map((item) => ({
      ...item,
      audit: auditPrice(item, { profilPricing, userProfil }),
    }));
  }, [items, profilPricing, userProfil]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.2 — détection alcool panier
  --------------------------------------------------------------------------
  */
  const hasAlcoholItem = useMemo(() => {
    return itemsAudites.some((item) => isAlcoholCartItem(item));
  }, [itemsAudites]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.3 — détection produits vendus au poids
  --------------------------------------------------------------------------
  | Cette information sert à afficher la notice métier expliquant que le
  | poids final préparé peut être légèrement supérieur au poids commandé,
  | sans jamais être inférieur.
  --------------------------------------------------------------------------
  */
  const hasWeightedItem = useMemo(() => {
    return itemsAudites.some((item) => isWeightedCartItem(item));
  }, [itemsAudites]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.4 — total affiché du panier
  --------------------------------------------------------------------------
  */
  const totalAfficheCents = useMemo(() => {
    return itemsAudites.reduce((acc, item) => {
      const grams = Number(item?.grams || 0) || 0;
      const gramsMultiplier = grams > 0 ? grams / 100 : 1;
      const price = Number(item.audit?.displayPrice || 0);
      const qty = Number(item.qty || item.quantite || 1);
      return acc + Math.round(price * gramsMultiplier) * qty;
    }, 0);
  }, [itemsAudites]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.5 — animation pulse du total
  --------------------------------------------------------------------------
  */
  const [isTotalPulsing, setIsTotalPulsing] = useState(false);
  const pulseTimerRef = useRef(null);

  useEffect(() => {
    setIsTotalPulsing(true);

    if (pulseTimerRef.current) {
      window.clearTimeout(pulseTimerRef.current);
    }

    pulseTimerRef.current = window.setTimeout(() => {
      setIsTotalPulsing(false);
    }, 320);

    return () => {
      if (pulseTimerRef.current) {
        window.clearTimeout(pulseTimerRef.current);
      }
    };
  }, [totalAfficheCents]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.6 — état local des recommandations panier
  --------------------------------------------------------------------------
  */
  const [cartRecommendations, setCartRecommendations] = useState([]);
  const [cartRecommendationsLoading, setCartRecommendationsLoading] =
    useState(false);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.7 — état local modale produit recommandé
  --------------------------------------------------------------------------
  */
  const [isRecoQuickViewOpen, setIsRecoQuickViewOpen] = useState(false);
  const [selectedRecoProduct, setSelectedRecoProduct] = useState(null);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.8 — payload minimal envoyé au backend recommandations
  --------------------------------------------------------------------------
  */
  const recommendationRequestItems = useMemo(() => {
    return (Array.isArray(itemsAudites) ? itemsAudites : []).map(
      buildRecommendationRequestItem
    );
  }, [itemsAudites]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.9 — index rapide des recommandations par sourceKey
  --------------------------------------------------------------------------
  */
  const recommendationsBySourceKey = useMemo(() => {
    const map = {};

    (Array.isArray(cartRecommendations) ? cartRecommendations : []).forEach(
      (entry) => {
        if (!entry?.sourceKey) return;
        map[entry.sourceKey] = entry;
      }
    );

    return map;
  }, [cartRecommendations]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.10 — fetch des recommandations
  --------------------------------------------------------------------------
  */
  useEffect(() => {
    let isMounted = true;

    const loadRecommendations = async () => {
      if (
        !Array.isArray(recommendationRequestItems) ||
        !recommendationRequestItems.length
      ) {
        if (isMounted) {
          setCartRecommendations([]);
        }
        return;
      }

      try {
        setCartRecommendationsLoading(true);

        const response = await fetch(
          "https://marchedeluxe.ch/services/get_cart_recommendations.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: recommendationRequestItems,
            }),
          }
        );

        const data = await response.json().catch(() => null);

        if (!isMounted) return;

        if (
          response.ok &&
          data?.success &&
          Array.isArray(data?.recommendations)
        ) {
          setCartRecommendations(data.recommendations);
        } else {
          setCartRecommendations([]);
        }
      } catch (error) {
        if (isMounted) {
          setCartRecommendations([]);
        }

        if (typeof console !== "undefined" && console.error) {
          console.error(
            "Impossible de charger les recommandations panier:",
            error
          );
        }
      } finally {
        if (isMounted) {
          setCartRecommendationsLoading(false);
        }
      }
    };

    loadRecommendations();

    return () => {
      isMounted = false;
    };
  }, [recommendationRequestItems]);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.11 — ajout au panier depuis une recommandation inline
  --------------------------------------------------------------------------
  */
  const handleAddRecommendation = useCallback(
    (product) => {
      if (!product) return;

      const normalizedCartProduct = normalizeRecommendedProductForCart(product);
      ajouterAuPanier(normalizedCartProduct, 1, 0);
    },
    [ajouterAuPanier]
  );

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.12 — ouverture modale produit recommandé
  --------------------------------------------------------------------------
  */
  const handleOpenRecommendation = useCallback((product) => {
    if (!product) return;

    const normalizedModalProduct = normalizeRecommendedProductForModal(product);

    setSelectedRecoProduct(normalizedModalProduct);
    setIsRecoQuickViewOpen(true);
  }, []);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.13 — fermeture modale produit recommandé
  --------------------------------------------------------------------------
  */
  const handleCloseRecommendationModal = useCallback(() => {
    setIsRecoQuickViewOpen(false);
    setSelectedRecoProduct(null);
  }, []);

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.14 — ajout au panier depuis la modale recommandation
  --------------------------------------------------------------------------
  | ProductQuickViewModal renvoie un payload orienté UI.
  | Ce bloc le convertit en vrai produit panier.
  --------------------------------------------------------------------------
  */
  const handleAddRecommendationFromModal = useCallback(
    (payload) => {
      const base = selectedRecoProduct;
      if (!base || !payload) return;

      /*
      ------------------------------------------------------------------------
      | Cas 1 — produit au poids libre
      ------------------------------------------------------------------------
      */
      if (Number(payload?.grams || 0) > 0) {
        ajouterAuPanier(
          {
            ...base,
            id: Number(payload?.productId || base.id),
            name: payload?.name || base.name,
            nom: payload?.name || base.name,
            image: payload?.imageUrl || base.image || base.imageUrl || "",
            imageUrl: payload?.imageUrl || base.imageUrl || base.image || "",
            image_url:
              payload?.imageUrl || base.image_url || base.imageUrl || "",
            price:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            displayPrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            appliedPrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            basePrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            publicPrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            unite: base?.unite || "",
          },
          1,
          Number(payload.grams)
        );

        return;
      }

      /*
      ------------------------------------------------------------------------
      | Cas 2 — produit à unités fixes
      ------------------------------------------------------------------------
      */
      if (Number(payload?.pieces || 0) > 0) {
        ajouterAuPanier(
          {
            ...base,
            id: Number(payload?.productId || base.id),
            name: payload?.name || base.name,
            nom: payload?.name || base.name,
            image: payload?.imageUrl || base.image || base.imageUrl || "",
            imageUrl: payload?.imageUrl || base.imageUrl || base.image || "",
            image_url:
              payload?.imageUrl || base.image_url || base.imageUrl || "",
            price:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            displayPrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            appliedPrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            basePrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            publicPrice:
              base?.price ?? Math.round(Number(payload?.priceCents || 0)) / 100,
            unite: base?.unite || "",
          },
          Number(payload.pieces),
          0
        );
      }
    },
    [ajouterAuPanier, selectedRecoProduct]
  );

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.15 — update quantité par delta
  --------------------------------------------------------------------------
  */
  const handleUpdateQty = useCallback(
    (id, delta, grams = null) => {
      const item = (Array.isArray(items) ? items : []).find(
        (i) =>
          String(i.id) === String(id) &&
          (grams == null || Number(i.grams || 0) === Number(grams))
      );

      if (!item) return;

      const currentQty = Number(item.qty || item.quantite || 1) || 1;
      const nextQty = Math.max(1, currentQty + delta);
      updateQuantite(id, nextQty, grams);
    },
    [items, updateQuantite]
  );

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.16 — rendu état loading profil
  --------------------------------------------------------------------------
  */
  if (authStatus === "loading") {
    return (
      <div className="loader-panier">
        {t.verifyingBenefits || "Vérification de vos avantages..."}
      </div>
    );
  }

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.17 — rendu panier vide
  --------------------------------------------------------------------------
  */
  if (!itemsAudites.length) {
    return (
      <>
        <div className="panier-vide">
          <h2 className="panier-vide-title">
            {t.emptyCart || "Votre sélection est vide"}
          </h2>

          <div className="panier-vide-actions">
            <button
              className="btn-luxury"
              onClick={() => setVue("magasin")}
              type="button"
            >
              {t.backToShop || "Retour au magasin"}
            </button>

            <button
              className="btn-luxury-outline"
              onClick={() => setVue("alcool")}
              type="button"
            >
              {t?.discoverCaveUniverse || "Découvrir l’univers cave"}
            </button>

            <button
              className="btn-luxury-outline"
              onClick={() => setVue("plateaux-evenements")}
              type="button"
            >
              {t?.discoverEventPlatters || "Découvrir nos plateaux événements"}
            </button>

            <button
              className="btn-back-link"
              onClick={() => setVue("compte")}
              type="button"
            >
              {t?.unpaidOrdersLink || "Voir mes commandes non payées"}
            </button>
          </div>
        </div>

        <style jsx="true">{`
          .panier-vide {
            width: 100%;
            min-height: 60vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 26px;
            padding: 40px 20px;
          }

          .panier-vide-title {
            font-family: "Playfair Display", serif;
            font-size: 26px;
            color: #1f1a17;
            margin: 0;
          }

          .panier-vide-actions {
            display: flex;
            flex-direction: column;
            gap: 14px;
            align-items: center;
            width: 100%;
            max-width: 340px;
          }

          .panier-vide-actions button {
            width: 100%;
          }

          @media (max-width: 768px) {
            .panier-vide-title {
              font-size: 22px;
            }
          }
        `}</style>
      </>
    );
  }

  /*
  --------------------------------------------------------------------------
  | Sous-bloc 22.18 — rendu principal panier rempli
  --------------------------------------------------------------------------
  */
  return (
    <>
      <div className="panier-container">
        <h1 className="panier-titre">{t.cartTitle || "VOTRE PANIER"}</h1>

        <div className="panier-content">
          <div className="panier-items">
            {itemsAudites.map((item) => (
              <PanierItem
                key={item.cartKey || `${item.id}-${item.grams || 0}`}
                item={item}
                onUpdateQty={handleUpdateQty}
                onRemove={retirerDuPanier}
                t={t}
                recommendationsBySourceKey={recommendationsBySourceKey}
                onAddRecommendation={handleAddRecommendation}
                onOpenRecommendation={handleOpenRecommendation}
              />
            ))}

            {cartRecommendationsLoading ? (
              <div className="cart-reco-loading">
                {t?.loadingRecommendations ||
                  "Suggestions en cours de chargement..."}
              </div>
            ) : null}
          </div>

          <div className="panier-summary">
            <h2>{t.summary || "RÉSUMÉ"}</h2>

            <div className="summary-line">
              <span>{t.subtotal || "Sous-total"}</span>
              <span>{formatCHF(totalAfficheCents)}</span>
            </div>

            {userProfil ? (
              <div className="member-savings-hint">
                <span className="gold-text">
                  {t.memberBenefitsApplied || "Avantages membres appliqués"}
                </span>
              </div>
            ) : null}

            {hasWeightedItem ? (
              <div className="panier-weight-notice">
                {t?.panierWeightNotice ||
                  "Les produits coupés au poids sont préparés avec soin en magasin. Le poids final peut être légèrement supérieur à la quantité commandée. Le prix facturé reste celui de la quantité commandée."}
              </div>
            ) : null}

            {hasAlcoholItem ? (
              <div className="panier-alcohol-notice">
                {t?.panierAlcoholNotice ||
                  "En validant cette commande, vous certifiez avoir au moins 18 ans pour l’achat de boissons alcoolisées."}
              </div>
            ) : null}

            <div className="summary-line total">
              <span>{t.total || "TOTAL"}</span>
              <span
                className={`total-amount ${isTotalPulsing ? "is-pulsing" : ""}`}
              >
                {formatCHF(totalAfficheCents)}
              </span>
            </div>

            <button
             className="btn-checkout"
             onClick={() => {
               trackFunnelEventBatch("begin_checkout", {
                cart_items:
                  Array.isArray(itemsAudites)
                  ? itemsAudites.length
                  : 0,

                cart_total_cents:
                   totalAfficheCents || 0,
                
                has_alcohol:
                  hasAlcoholItem === true,

                has_weighted:
                  hasWeightedItem === true
              });
             setVue("tunnel");
          }}
           type="button"
        >
          {t.checkout || "COMMANDER"}
          
          </button>

            <button
              className="btn-back-link"
              onClick={() => setVue("magasin")}
              type="button"
            >
              {t.backToShop || "← Continuer mes achats"}
            </button>
          </div>
        </div>
      </div>

      <ProductQuickViewModal
        isOpen={isRecoQuickViewOpen}
        onClose={handleCloseRecommendationModal}
        product={selectedRecoProduct}
        onAddToCart={handleAddRecommendationFromModal}
      />

      <style jsx="true">{`
        .panier-item-shell {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 18px;
        }

        .panier-item {
          grid-template-columns: 140px minmax(0, 1fr) auto;
          gap: 24px;
          align-items: start;
          max-height: none;
          overflow: visible;
        }

        .panier-item-media {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          min-width: 140px;
        }

        .panier-item-media .item-img {
          width: 140px;
          height: 140px;
          flex: 0 0 auto;
        }

        .panier-item-media .item-quantity {
          justify-content: center;
          width: fit-content;
          max-width: 140px;
          margin: 0 auto;
        }

        .panier-item-info {
          min-width: 0;
          padding-top: 2px;
        }

        .panier-item-info h3 {
          margin-top: 0;
          margin-right: 18px;
        }

        .panier-item-price {
          margin-bottom: 0;
          line-height: 1.45;
        }

        .panier-unit-secondary {
          margin-left: 8px;
          opacity: 0.85;
          white-space: nowrap;
        }

        .panier-inline-summary {
          margin-top: 12px;
          display: flex;
          align-items: baseline;
          gap: 16px;
          flex-wrap: wrap;
        }

        .panier-inline-quantity {
          font-family: var(--font-montserrat);
          font-size: 1rem;
          font-weight: 700;
          color: rgba(0, 0, 0, 0.76);
        }

        .panier-inline-total {
          font-family: var(--font-serif);
          font-size: 1.35rem;
          line-height: 1;
          font-weight: 700;
          letter-spacing: 0.5px;
          color: #111;
        }

        .panier-container .btn-remove {
          align-self: start;
          justify-self: end;
          margin-top: 2px;
        }

        .cart-reco-inline-block {
          margin-left: 164px;
          margin-top: -2px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding: 2px 0 0;
        }

        .cart-reco-inline-title {
          font-size: 14px;
          font-weight: 600;
          color: #8a7357;
          white-space: nowrap;
        }

        .cart-reco-inline-list {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .cart-reco-inline-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
        }

        .cart-reco-inline-link {
          border: none;
          background: transparent;
          padding: 0;
          margin: 0;
          cursor: pointer;
          color: #1f1a17;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.3;
          text-align: left;
          text-decoration: none;
        }

        .cart-reco-inline-link:hover {
          color: #8a7357;
        }

        .cart-reco-inline-add {
          width: 24px;
          height: 24px;
          border-radius: 999px;
          border: 1px solid rgba(31, 26, 23, 0.16);
          background: #fff;
          color: #1f1a17;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          line-height: 1;
          padding: 0;
          transition: transform 0.18s ease, background 0.18s ease;
        }

        .cart-reco-inline-add:hover {
          transform: translateY(-1px);
          background: #f6f1ea;
        }

        .cart-reco-loading {
          margin-top: 8px;
          font-size: 13px;
          color: #7a6a59;
        }

        .panier-weight-notice {
          margin: 14px 0 4px;
          padding: 12px 14px;
          border-radius: 14px;
          background: linear-gradient(
            180deg,
            rgba(248, 246, 241, 0.98) 0%,
            rgba(242, 237, 229, 0.98) 100%
          );
          border: 1px solid rgba(145, 124, 95, 0.18);
          color: #5c4b38;
          font-size: 13px;
          line-height: 1.55;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.04);
        }

        .panier-alcohol-notice {
          margin: 14px 0 4px;
          padding: 12px 14px;
          border-radius: 14px;
          background: linear-gradient(
            180deg,
            rgba(255, 248, 236, 0.98) 0%,
            rgba(250, 242, 223, 0.98) 100%
          );
          border: 1px solid rgba(212, 175, 55, 0.22);
          color: #5b4630;
          font-size: 13px;
          line-height: 1.55;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.04);
        }

        @media (max-width: 980px) {
          .panier-item {
            grid-template-columns: 88px minmax(0, 1fr) auto;
            grid-template-areas: "media info remove";
            gap: 12px 14px;
            align-items: start;
            overflow: visible;
          }

          .panier-item-media {
            grid-area: media;
            min-width: 88px;
            gap: 10px;
            align-items: flex-start;
          }

          .panier-item-media .item-img {
            width: 88px;
            height: 88px;
          }

          .panier-item-media .item-quantity {
            transform: none;
            max-width: 88px;
            padding: 6px 8px;
            gap: 8px;
          }

          .panier-item-info {
            grid-area: info;
            min-width: 0;
          }

          .panier-item-info h3 {
            margin-right: 10px;
          }

          .panier-inline-summary {
            margin-top: 8px;
            gap: 10px;
          }

          .panier-inline-quantity {
            font-size: 0.94rem;
          }

          .panier-inline-total {
            font-size: 1.08rem;
          }

          .panier-container .btn-remove {
            grid-area: remove;
            justify-self: end;
            align-self: start;
          }

          .panier-weight-notice {
            font-size: 12.5px;
            line-height: 1.5;
            padding: 11px 12px;
          }

          .panier-alcohol-notice {
            font-size: 12.5px;
            line-height: 1.5;
            padding: 11px 12px;
          }

          .cart-reco-inline-block {
            margin-left: 102px;
            gap: 10px;
          }

          .cart-reco-inline-title,
          .cart-reco-inline-link {
            font-size: 13px;
          }
        }

        @media (max-width: 520px) {
          .panier-item {
            grid-template-columns: 76px minmax(0, 1fr) auto;
            padding: 14px 12px;
          }

          .panier-item-media {
            min-width: 76px;
            gap: 8px;
          }

          .panier-item-media .item-img {
            width: 76px;
            height: 76px;
          }

          .panier-item-media .item-quantity {
            max-width: 76px;
            padding: 5px 6px;
            gap: 6px;
            transform: none;
          }

          .panier-unit-secondary {
            display: inline;
            margin-left: 6px;
          }

          .panier-inline-summary {
            margin-top: 7px;
            gap: 8px;
          }

          .panier-inline-quantity {
            font-size: 0.9rem;
          }

          .panier-inline-total {
            font-size: 1rem;
          }

          .cart-reco-inline-block {
            margin-left: 0;
            gap: 8px;
          }

          .cart-reco-inline-title,
          .cart-reco-inline-link {
            font-size: 13px;
          }

          .cart-reco-inline-add {
            width: 22px;
            height: 22px;
            font-size: 15px;
          }
        }
        .panier-fondue-details {
          margin-top: 4px;
          color: #4f4032;
        }

        .panier-fondue-ratios {
          margin-top: 6px;
          color: #6a5948;
          line-height: 1.45;
        }
        /* =========================
   RÉSUMÉ — TYPO LUXE
========================= */
        .panier-summary h2,
        .panier-summary .summary-line span,
        .panier-summary .summary-line.total span,
        .panier-summary .member-savings-hint {
          font-family: "Playfair Display", "Times New Roman", serif;
        }

        .panier-summary .summary-line span:first-child {
          font-size: 0.95rem;
          letter-spacing: 0.04em;
          color: #6f5c45;
        }

        .panier-summary .summary-line span:last-child {
          font-size: 1.25rem;
          font-weight: 700;
          color: #241c17;
        }

        .panier-summary .summary-line.total span:last-child {
          font-size: 1.6rem;
          letter-spacing: 0.02em;
        }

        .panier-summary .member-savings-hint {
          font-size: 0.85rem;
          color: #b08a45;
          margin-top: 6px;
        }
      `}</style>
    </>
  );
};

export default PagePanier;
